import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../auth/authStore';
import {
	adminSearchUsersService,
	AdminSwitchUserPayload,
	adminSwitchUserService,
	AdminUser,
} from './adminService';

export function useAdminSearchUsers() {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [users, setUsers] = useState<AdminUser[]>([]);

	const searchUsers = async (emailPattern: string) => {
		if (!emailPattern.trim()) {
			setUsers([]);
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			const response = await adminSearchUsersService(emailPattern);
			setUsers(response.data.users);
			return { users: response.data.users, error: null };
		} catch (err: any) {
			const errorMessage = err.response?.data?.message || 'Failed to search users';
			setError(errorMessage);
			setUsers([]);
			return { users: [], error: errorMessage };
		} finally {
			setIsLoading(false);
		}
	};

	return { searchUsers, users, isLoading, error, setUsers };
}

export function useAdminSwitchUser() {
	const { setAccessToken } = useAuthStore();
	const { t } = useTranslation();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const switchToUser = async (payload: AdminSwitchUserPayload) => {
		setIsLoading(true);
		setError(null);

		try {
			const response = await adminSwitchUserService(payload);
			const { accessToken, refreshToken } = response.data;

			// Save new tokens
			setAccessToken(accessToken, refreshToken);

			// Get current language for navigation
			const currentLanguage = localStorage.getItem('i18nextLng') || 'en';

			// Navigate based on user type
			let targetPath = '';
			if (payload.userType === 'specialist') {
				targetPath = `/${currentLanguage}/specialist-switcher`;
			} else {
				targetPath = `/${currentLanguage}/${payload.userType}/dashboard`;
			}

			showSnackbar(
				t('success'),
				`Successfully switched to ${payload.userType}: ${payload.email}`,
				'success'
			);

			// Redirect after a short delay
			setTimeout(() => {
				window.location.href = targetPath;
			}, 1000);

			return { success: true, error: null };
		} catch (err: any) {
			const errorMessage = err.response?.data?.message || 'Failed to switch user';
			setError(errorMessage);
			showSnackbar(t('error'), errorMessage, 'error');
			return { success: false, error: errorMessage };
		} finally {
			setIsLoading(false);
		}
	};

	return { switchToUser, isLoading, error };
}
